import DashboardLayout from "@/components/DashboardLayout";
import { TabbedCodeBlock } from "@/components/TabbedCodeBlock";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/ui/empty";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { trackEvent } from "@/lib/analytics";
import { trpc } from "@/lib/trpc";
import { LANGUAGES, type Language, virtualEnvSnippets } from "@/snippets";
import { ArrowLeft, Copy, Key, Plus, Trash2, Clock, Shield, Globe, FolderKey } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useLocation, useParams } from "wouter";

export default function VirtualEnvDetail() {
  const { id } = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const virtualEnvId = parseInt(id || "0");

  const [isGenerateOpen, setIsGenerateOpen] = useState(false);
  const [ttlSeconds, setTtlSeconds] = useState("3600");
  const [generatedToken, setGeneratedToken] = useState<string | null>(null);
  
  const [isAddSecretOpen, setIsAddSecretOpen] = useState(false);
  const [selectedSecretId, setSelectedSecretId] = useState<string>("");
  const [envVarName, setEnvVarName] = useState("");
  
  const [newIp, setNewIp] = useState("");
  const [newIpDescription, setNewIpDescription] = useState("");
  const [isAddIpOpen, setIsAddIpOpen] = useState(false);

  const { data: virtualEnv, isLoading, refetch: refetchEnv } = trpc.virtualEnv.get.useQuery({ id: virtualEnvId });
  const { data: veSecrets, refetch: refetchSecrets } = trpc.virtualEnv.getSecrets.useQuery({ virtualEnvId });
  const { data: tokens, refetch: refetchTokens } = trpc.virtualEnv.listTokens.useQuery({ virtualEnvId });
  const { data: ipWhitelist, refetch: refetchIpWhitelist } = trpc.virtualEnv.listIpWhitelist.useQuery({ virtualEnvId });
  const { data: availableSecrets } = trpc.secrets.list.useQuery();
  const { data: savedIps } = trpc.savedIps.list.useQuery();
  const [selectedSavedIp, setSelectedSavedIp] = useState<string>("");

  const generateMutation = trpc.virtualEnv.generateToken.useMutation({
    onSuccess: (data) => {
      toast.success("Token generated successfully");
      setGeneratedToken(data.token);
      refetchTokens();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const deleteMutation = trpc.virtualEnv.delete.useMutation({
    onSuccess: () => {
      trackEvent("virtual_env_deleted");
      toast.success("Virtual .env deleted successfully");
      setLocation("/virtual-env");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const addSecretMutation = trpc.virtualEnv.addSecret.useMutation({
    onSuccess: () => {
      toast.success("Secret added successfully");
      setIsAddSecretOpen(false);
      setSelectedSecretId("");
      setEnvVarName("");
      refetchSecrets();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const removeSecretMutation = trpc.virtualEnv.removeSecret.useMutation({
    onSuccess: () => {
      toast.success("Secret removed successfully");
      refetchSecrets();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const addIpMutation = trpc.virtualEnv.addIpToWhitelist.useMutation({
    onSuccess: () => {
      toast.success("IP added to whitelist");
      setNewIp("");
      setNewIpDescription("");
      setIsAddIpOpen(false);
      refetchIpWhitelist();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const removeIpMutation = trpc.virtualEnv.removeIpFromWhitelist.useMutation({
    onSuccess: () => {
      toast.success("IP removed from whitelist");
      refetchIpWhitelist();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleGenerate = () => {
    const ttl = parseInt(ttlSeconds);
    if (isNaN(ttl) || ttl < 60 || ttl > 86400 * 30) {
      toast.error("TTL must be between 60 seconds and 30 days");
      return;
    }

    generateMutation.mutate({
      virtualEnvId,
      ttlSeconds: ttl,
    });
  };

  const handleCopyToken = (token: string) => {
    navigator.clipboard.writeText(token);
    toast.success("Token copied to clipboard");
  };

  const handleDelete = () => {
    if (confirm("Are you sure you want to delete this virtual .env? This action cannot be undone.")) {
      deleteMutation.mutate({ id: virtualEnvId });
    }
  };

  const handleAddSecret = () => {
    if (!selectedSecretId) {
      toast.error("Please select a secret");
      return;
    }
    addSecretMutation.mutate({
      virtualEnvId,
      secretId: parseInt(selectedSecretId),
      envVarName: envVarName.trim() || undefined,
    });
  };

  const handleRemoveSecret = (veSecretId: number) => {
    if (confirm("Are you sure you want to remove this secret from the virtual .env?")) {
      removeSecretMutation.mutate({ virtualEnvSecretId: veSecretId, virtualEnvId });
    }
  };

  const handleAddIp = () => {
    if (!newIp.trim()) {
      toast.error("Please enter an IP address");
      return;
    }
    addIpMutation.mutate({
      virtualEnvId,
      ipAddress: newIp.trim(),
      description: newIpDescription.trim() || undefined,
    });
  };

  const handleRemoveIp = (ipId: number) => {
    if (confirm("Are you sure you want to remove this IP from the whitelist?")) {
      removeIpMutation.mutate({ ipId, virtualEnvId });
    }
  };

  const formatTTL = (seconds: number) => {
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours`;
    return `${Math.floor(seconds / 86400)} days`;
  };

  const snippetConfig = {
    token: "YOUR_TOKEN_HERE",
    baseUrl: window.location.origin,
  };

  // Get secrets that aren't already in this virtual env
  const availableToAdd = availableSecrets?.filter(
    secret => !veSecrets?.some(ves => ves.virtualEnvSecrets.secretId === secret.id)
  ) || [];

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center py-12">
          <Spinner className="size-8 text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (!virtualEnv) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold mb-2">Virtual .env not found</h2>
          <Button onClick={() => setLocation("/virtual-env")}>Back to Virtual Envs</Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => setLocation("/virtual-env")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold text-foreground">{virtualEnv.name}</h1>
            </div>
            {virtualEnv.description && (
              <p className="text-muted-foreground mt-1">{virtualEnv.description}</p>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="destructive" onClick={handleDelete}>
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>

        <Tabs defaultValue="secrets" className="space-y-4">
          <TabsList>
            <TabsTrigger value="secrets">Secrets</TabsTrigger>
            <TabsTrigger value="tokens">Tokens</TabsTrigger>
            <TabsTrigger value="security">Security</TabsTrigger>
            <TabsTrigger value="usage">How to Use</TabsTrigger>
          </TabsList>

          <TabsContent value="secrets" className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Secrets</h3>
                <p className="text-sm text-muted-foreground">Manage which secrets are included in this virtual .env</p>
              </div>
              <Dialog open={isAddSecretOpen} onOpenChange={setIsAddSecretOpen}>
                <DialogTrigger asChild>
                  <Button disabled={availableToAdd.length === 0}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Secret
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Add Secret to Virtual .env</DialogTitle>
                    <DialogDescription>
                      Select a secret to add to this virtual environment. You can optionally specify a custom environment variable name.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Select Secret *</Label>
                      <Select value={selectedSecretId} onValueChange={setSelectedSecretId}>
                        <SelectTrigger>
                          <SelectValue placeholder="Choose a secret..." />
                        </SelectTrigger>
                        <SelectContent>
                          {availableToAdd.map((secret) => (
                            <SelectItem key={secret.id} value={secret.id.toString()}>
                              {secret.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="envVarName">Environment Variable Name (optional)</Label>
                      <Input
                        id="envVarName"
                        placeholder="e.g., OPENAI_API_KEY"
                        value={envVarName}
                        onChange={(e) => setEnvVarName(e.target.value.toUpperCase())}
                        className="font-mono"
                      />
                      <p className="text-xs text-muted-foreground">
                        Leave empty to auto-generate from secret name
                      </p>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setIsAddSecretOpen(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleAddSecret} disabled={addSecretMutation.isPending}>
                      {addSecretMutation.isPending ? "Adding..." : "Add Secret"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            {veSecrets && veSecrets.length > 0 ? (
              <div className="grid gap-4">
                {veSecrets.map((ves) => (
                  <Card key={ves.virtualEnvSecrets.id}>
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Key className="w-5 h-5 text-primary" />
                          <div>
                            <p className="font-medium">{ves.secrets.name}</p>
                            <p className="text-sm text-muted-foreground font-mono">{ves.virtualEnvSecrets.envVarName}</p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveSecret(ves.virtualEnvSecrets.id)}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon"><Key /></EmptyMedia>
                  <EmptyTitle>No secrets yet</EmptyTitle>
                  <EmptyDescription>
                    Add secrets to include them in this virtual environment.
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            )}
          </TabsContent>

          <TabsContent value="tokens" className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Access Tokens</h3>
                <p className="text-sm text-muted-foreground">Generate tokens to access all secrets in this virtual .env</p>
              </div>
              <Dialog open={isGenerateOpen} onOpenChange={setIsGenerateOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="w-4 h-4 mr-2" />
                    Generate Token
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Generate Access Token</DialogTitle>
                    <DialogDescription>
                      Create a token to access all secrets in this virtual .env. The token will expire after the specified TTL.
                    </DialogDescription>
                  </DialogHeader>
                  {generatedToken ? (
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Your Token</Label>
                        <div className="flex gap-2">
                          <Input
                            value={generatedToken}
                            readOnly
                            className="font-mono text-sm"
                          />
                          <Button size="icon" onClick={() => handleCopyToken(generatedToken)}>
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Copy this token now. You won't be able to see it again.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="ttl">Time to Live (TTL)</Label>
                        <Select value={ttlSeconds} onValueChange={setTtlSeconds}>
                          <SelectTrigger id="ttl">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="3600">1 hour</SelectItem>
                            <SelectItem value="21600">6 hours</SelectItem>
                            <SelectItem value="86400">1 day</SelectItem>
                            <SelectItem value="604800">7 days</SelectItem>
                            <SelectItem value="2592000">30 days</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                  <DialogFooter>
                    {generatedToken ? (
                      <Button onClick={() => {
                        setIsGenerateOpen(false);
                        setGeneratedToken(null);
                      }}>
                        Done
                      </Button>
                    ) : (
                      <>
                        <Button variant="outline" onClick={() => setIsGenerateOpen(false)}>
                          Cancel
                        </Button>
                        <Button onClick={handleGenerate} disabled={generateMutation.isPending}>
                          {generateMutation.isPending ? "Generating..." : "Generate"}
                        </Button>
                      </>
                    )}
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            {tokens && tokens.length > 0 ? (
              <div className="grid gap-4">
                {tokens.map((token) => {
                  const isExpired = new Date(token.expiresAt) < new Date();
                  return (
                    <Card key={token.id}>
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <CardTitle className="text-base font-mono">
                              {token.token.substring(0, 20)}...
                            </CardTitle>
                            <CardDescription className="mt-1">
                              Created {new Date(token.createdAt).toLocaleString()}
                            </CardDescription>
                          </div>
                          <div className={`px-2 py-1 rounded text-xs font-medium ${
                            isExpired 
                              ? "bg-destructive/10 text-destructive" 
                              : "bg-primary/10 text-primary"
                          }`}>
                            {isExpired ? "Expired" : "Active"}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-muted-foreground">TTL</p>
                            <p className="font-medium">{formatTTL(token.ttlSeconds)}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Expires</p>
                            <p className="font-medium">{new Date(token.expiresAt).toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Usage Count</p>
                            <p className="font-medium">{token.usageCount}</p>
                          </div>
                          {token.lastUsedAt && (
                            <div>
                              <p className="text-muted-foreground">Last Used</p>
                              <p className="font-medium">{new Date(token.lastUsedAt).toLocaleString()}</p>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon"><FolderKey /></EmptyMedia>
                  <EmptyTitle>No tokens yet</EmptyTitle>
                  <EmptyDescription>
                    Generate a token to start accessing this virtual .env in your code.
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            )}
          </TabsContent>

          <TabsContent value="security" className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">IP Whitelist</h3>
                <p className="text-sm text-muted-foreground">
                  Restrict access to specific IP addresses. If empty, IP restrictions from individual secrets apply.
                </p>
              </div>
              <Dialog open={isAddIpOpen} onOpenChange={setIsAddIpOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="w-4 h-4 mr-2" />
                    Add IP
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Add IP to Whitelist</DialogTitle>
                    <DialogDescription>
                      Add an IP address that will be allowed to access this virtual .env.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    {/* Saved IPs Dropdown */}
                    {savedIps && savedIps.length > 0 && (
                      <div className="space-y-2">
                        <Label>Select from Saved IPs</Label>
                        <Select
                          value={selectedSavedIp}
                          onValueChange={(value) => {
                            setSelectedSavedIp(value);
                            const selected = savedIps.find(ip => ip.id.toString() === value);
                            if (selected) {
                              setNewIp(selected.ipAddress);
                              setNewIpDescription(selected.name);
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Choose a saved IP..." />
                          </SelectTrigger>
                          <SelectContent>
                            {savedIps.map((ip) => (
                              <SelectItem key={ip.id} value={ip.id.toString()}>
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{ip.name}</span>
                                  <span className="text-muted-foreground font-mono text-sm">({ip.ipAddress})</span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          Or enter a new IP address below
                        </p>
                      </div>
                    )}
                    
                    <div className="space-y-2">
                      <Label htmlFor="ip">IP Address</Label>
                      <Input
                        id="ip"
                        placeholder="e.g., 192.168.1.1"
                        value={newIp}
                        onChange={(e) => {
                          setNewIp(e.target.value);
                          setSelectedSavedIp("");
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ipDesc">Description (optional)</Label>
                      <Input
                        id="ipDesc"
                        placeholder="e.g., Office network"
                        value={newIpDescription}
                        onChange={(e) => setNewIpDescription(e.target.value)}
                      />
                    </div>
                    
                    {/* Find My IP Section */}
                    <div className="border-t pt-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Globe className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm font-medium">Find My IP</span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          try {
                            const response = await fetch('https://api.ipify.org?format=json');
                            const data = await response.json();
                            setNewIp(data.ip);
                            setSelectedSavedIp("");
                            toast.success(`Your IP: ${data.ip}`);
                          } catch {
                            toast.error("Failed to detect IP address");
                          }
                        }}
                      >
                        Detect My Current IP
                      </Button>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setIsAddIpOpen(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleAddIp} disabled={addIpMutation.isPending}>
                      {addIpMutation.isPending ? "Adding..." : "Add IP"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            {ipWhitelist && ipWhitelist.length > 0 ? (
              <div className="grid gap-4">
                {ipWhitelist.map((ip) => (
                  <Card key={ip.id}>
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Shield className="w-5 h-5 text-primary" />
                          <div>
                            <p className="font-mono font-medium">{ip.ipAddress}</p>
                            {ip.description && (
                              <p className="text-sm text-muted-foreground">{ip.description}</p>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveIp(ip.id)}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon"><Shield /></EmptyMedia>
                  <EmptyTitle>No IP restrictions</EmptyTitle>
                  <EmptyDescription>
                    IP restrictions from individual secrets will apply. Add IPs here to override.
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            )}
          </TabsContent>

          <TabsContent value="usage" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>How to Use This Virtual .env</CardTitle>
                <CardDescription>
                  Use the following Python code to fetch all secrets and set them as environment variables.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="mb-2 block">Code Snippet</Label>
                  <TabbedCodeBlock snippets={Object.fromEntries(
                    LANGUAGES.map(({ value }) => [value, virtualEnvSnippets[value](snippetConfig)])
                  ) as Record<Language, string>} />
                </div>
                
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2">Steps:</h4>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                    <li>Add secrets to this virtual .env from the "Secrets" tab</li>
                    <li>Generate a token from the "Tokens" tab</li>
                    <li>Copy the token and replace "YOUR_TOKEN_HERE" in the code</li>
                    <li>Run the code in your Python environment</li>
                    <li>All secrets will be fetched as a dictionary or loaded as environment variables</li>
                  </ol>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2">Response Format:</h4>
                  <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm font-mono">
{`{
  "name": "${virtualEnv.name}",
  "secrets": {
    ${veSecrets?.map(ves => `"${ves.virtualEnvSecrets.envVarName}": "***"`).join(',\n    ') || '"ENV_VAR_NAME": "secret_value"'}
  }
}`}
                  </pre>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}

